/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { noop } from 'lodash'
import React from 'react'

import { EuiFlexGroup, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useToggle } from '@modules/utils/hooks/useToggle'

// eslint-disable-next-line import/no-restricted-paths
import RolePanel from '@/components/Users/RoleAssignmentsPanel/RolePanel'

import CCMAssignmentsSearchableTable from './CCMAssignmentsSearchableTable'
import { CCMRoleDropdown } from './CCMRoleDropdown'

import type { FC } from 'react'
import type { CCMRolePanelProps } from './types'

const CCMRolePanel: FC<CCMRolePanelProps> = ({ initiallyChecked, roleSelectionOverride }) => {
  const [isChecked, toggleChecked] = useToggle(initiallyChecked)

  return (
    <RolePanel
      title={
        <FormattedMessage
          id='roles-assignments.ccm-access'
          defaultMessage='Connected cluster access'
        />
      }
      description={
        <FormattedMessage
          id='roles-assignments.ccm-access-description'
          defaultMessage='Grant access to services for connected clusters.'
        />
      }
      isChecked={isChecked || roleSelectionOverride}
      isDisabled={roleSelectionOverride}
      testId='cloud-connected-cluster-access'
      onChangeRole={toggleChecked}
    >
      {isChecked && (
        <EuiFlexGroup direction='column' gutterSize='l'>
          <EuiSpacer size='s' />
          <EuiText color='subdued' size='s'>
            <p>
              <FormattedMessage
                id='roles-assignments.ccm-access.description'
                defaultMessage='Set a role for all Connected clusters, or a specific role individually for each cluster.'
              />
            </p>
          </EuiText>
          {/* TODO: https://github.com/elastic/cloud/issues/142677 wire up global and individual cluster role assignment change logic when API and types are ready */}
          {/* TODO: https://github.com/elastic/cloud/issues/142677 wire up item fetching from API */}
          <CCMRoleDropdown onRoleChange={noop} />
          <CCMAssignmentsSearchableTable items={[]} onChangeCCMRoleAssignments={noop} />
        </EuiFlexGroup>
      )}
    </RolePanel>
  )
}

export default CCMRolePanel
